import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import '../../assets/css/project-chart.css';

const R  = 70;
const CX = 100;
const CY = 100;
const C  = 2 * Math.PI * R; // ≈ 439.82

function fmtDuration(ms) {
  if (!ms || ms <= 0) return '0h 00m';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function toNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

export default function ProjectChart({ token, workspace_id }) {
  useTranslation();
  const [filter, setFilter]   = useState('week');
  const [ready,  setReady]    = useState(false);
  const [data,   setData]     = useState([]);

  const fetchChartData = useCallback(async (selectedFilter) => {
    if (!workspace_id) return;

    let startDate;
    if      (selectedFilter === 'today') startDate = moment().startOf('day').unix();
    else if (selectedFilter === 'week')  startDate = moment().startOf('isoWeek').unix();
    else                                 startDate = moment().startOf('month').unix();

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'null' && token !== 'undefined') {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await axios.post(
        '/api/usertime/chart',
        { start_date: startDate, workspace_id },
        { headers, withCredentials: true }
      );
      setData(Array.isArray(res.data.results) ? res.data.results : []);
    } catch (err) {
      console.error('ProjectChart fetch error:', err);
    }
  }, [token, workspace_id]);

  // Fetch whenever filter, token, or workspace changes
  useEffect(() => {
    fetchChartData(filter);
  }, [filter, fetchChartData]);

  // Re-trigger draw animation after data loads
  useEffect(() => {
    setReady(false);
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, [filter, data]);

  const stats = useMemo(() => {
    if (!data || !data.length) return [];

    const map = {};
    data.forEach(entry => {
      const isStopped = Number(entry.isStop) === 1 || entry.isStop === true || entry.isStop === '1';
      if (!isStopped) return;
      const key   = entry.project_id ?? entry.project_name ?? 0;
      const name  = entry.project_name  || 'No Project';
      const color = entry.project_color || '#9ca3af';
      if (!map[key]) map[key] = { id: key, name, color, ms: 0 };
      map[key].ms += toNumber(entry.timerTime || entry.timer_time || 0);
    });

    return Object.values(map).filter(p => p.ms > 0).sort((a, b) => b.ms - a.ms);
  }, [data]);

  const totalMs = stats.reduce((s, p) => s + p.ms, 0);

  // Build SVG segment params
  const segments = useMemo(() => {
    if (!totalMs) return [];
    let acc = 0;
    return stats.map((p, i) => {
      const arc    = (p.ms / totalMs) * C;
      const offset = -acc;
      acc += arc;
      return { ...p, arc, offset, pct: Math.round((p.ms / totalMs) * 100) };
    });
  }, [stats, totalMs]);

  const FILTERS = [
    { key: 'today', get label() { return tr("Today"); }      },
    { key: 'week',  get label() { return tr("This Week"); }  },
    { key: 'month', get label() { return tr("This Month"); } },
  ];

  return (
    <div className="proj-chart-card">
      {/* Header */}
      <div className="proj-chart-header">
        <div>
          <h3 className="proj-chart-title">{tr("Project Statistics")}</h3>
          <p className="proj-chart-sub">{tr("Hours tracked per project")}</p>
        </div>
        <div className="proj-chart-filters">
          {FILTERS.map(f => (
            <button
              key={f.key}
              className={`proj-filter-btn${filter === f.key ? ' active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="proj-chart-body">

        {/* ── Donut chart ── */}
        <div className="proj-donut-wrap">
          <svg viewBox="0 0 200 200" className="proj-donut-svg">
            {/* Track ring */}
            <circle cx={CX} cy={CY} r={R} fill="none"
              stroke="#e8ede8" strokeWidth="24" />

            {segments.length === 0 ? (
              <circle cx={CX} cy={CY} r={R} fill="none"
                stroke="#d1d5db" strokeWidth="24"
                strokeDasharray={`${C} 0`}
                transform={`rotate(-90 ${CX} ${CY})`} />
            ) : segments.map((seg, i) => (
              <circle
                key={`${filter}-${i}`}
                cx={CX} cy={CY} r={R}
                fill="none"
                stroke={seg.color}
                strokeWidth="24"
                strokeLinecap="butt"
                /* animate: start invisible (0 arc), expand to real arc */
                strokeDasharray={ready ? `${seg.arc} ${C - seg.arc}` : `0 ${C}`}
                strokeDashoffset={seg.offset}
                transform={`rotate(-90 ${CX} ${CY})`}
                className="proj-donut-seg"
                style={{ transitionDelay: `${i * 0.11}s` }}
              />
            ))}

            {/* Centre text */}
            <text x={CX} y={CY - 8}  textAnchor="middle" className="proj-donut-total">
              {fmtDuration(totalMs)}
            </text>
            <text x={CX} y={CY + 13} textAnchor="middle" className="proj-donut-label">{tr("TOTAL")} </text>
          </svg>
        </div>

        {/* ── Project rows ── */}
        <div className="proj-list">
          {stats.length === 0 ? (
            <div className="proj-list-empty">{tr("No tracked time for this period.")}</div>
          ) : stats.map((p, i) => {
            const pct = totalMs > 0 ? (p.ms / totalMs) * 100 : 0;
            return (
              <div
                key={p.id}
                className={`proj-list-item${ready ? ' proj-list-item--in' : ''}`}
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div className="proj-list-top">
                  <span className="proj-list-dot" style={{ background: p.color }} />
                  <span className="proj-list-name">{p.name}</span>
                  <span className="proj-list-time">{fmtDuration(p.ms)}</span>
                  <span className="proj-list-pct">{Math.round(pct)}%</span>
                </div>
                <div className="proj-list-track">
                  <div
                    className="proj-list-fill"
                    style={{
                      background: p.color,
                      width: ready ? `${pct}%` : '0%',
                      transitionDelay: `${i * 0.08 + 0.25}s`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
